/**
 * Admin → Tenants (super_admin only).
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Pencil, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

import { tenantsApi } from "@/api/tenants";
import { apiErrorMessage } from "@/api/client";
import { qk, useTenants } from "@/hooks/queries";
import type { Tenant, TenantCreate } from "@/types";

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/Button";
import { DataTable, type Column } from "@/components/DataTable";
import { Modal } from "@/components/Modal";
import { Field, Input } from "@/components/Field";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { formatDate, shortId } from "@/lib/format";

export function AdminTenantsPage() {
  const tenants = useTenants();
  const qc = useQueryClient();

  const [editTarget, setEditTarget] = useState<Tenant | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Tenant | null>(null);

  const createMut = useMutation({
    mutationFn: (p: TenantCreate) => tenantsApi.create(p),
    onSuccess: () => {
      toast.success("Tenant criado");
      void qc.invalidateQueries({ queryKey: qk.tenants() });
      setIsCreating(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: TenantCreate }) =>
      tenantsApi.update(id, p),
    onSuccess: () => {
      toast.success("Tenant atualizado");
      void qc.invalidateQueries({ queryKey: qk.tenants() });
      setEditTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => tenantsApi.remove(id),
    onSuccess: () => {
      toast.success("Tenant deletado");
      void qc.invalidateQueries({ queryKey: qk.tenants() });
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const columns: Column<Tenant>[] = [
    { key: "name", header: "Nome", cell: (r) => <span className="font-medium text-slate-900 dark:text-slate-100">{r.name}</span> },
    { key: "slug", header: "Slug", cell: (r) => <code className="text-xs text-slate-600 dark:text-slate-400">{r.slug}</code> },
    { key: "id", header: "ID", cell: (r) => <code className="text-xs text-slate-500 dark:text-slate-400">{shortId(r.id)}</code>, hideOnMobile: true },
    { key: "created_at", header: "Criado em", cell: (r) => <span className="text-xs text-slate-500 dark:text-slate-400">{formatDate(r.created_at)}</span>, hideOnMobile: true },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <div className="flex justify-end gap-1">
          <Link to={`/admin/tenants/${r.id}/edit`}>
            <Button size="sm" variant="ghost" icon={<Building2 className="h-4 w-4" />}>
              Cadastro
            </Button>
          </Link>
          <Button size="sm" variant="ghost" icon={<Pencil className="h-4 w-4" />} onClick={() => setEditTarget(r)}>
            Renomear
          </Button>
          <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4 text-red-500" />} onClick={() => setDeleteTarget(r)} />
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Tenants"
        description="Clientes (organizações) que usam o portal."
        actions={
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => setIsCreating(true)}>
            Novo tenant
          </Button>
        }
      />

      <DataTable
        data={tenants.data}
        columns={columns}
        loading={tenants.isLoading}
        rowKey={(r) => r.id}
        emptyTitle="Nenhum tenant cadastrado"
        emptyDescription="Crie o primeiro tenant pra começar a configurar sites e ativos."
      />

      <TenantFormModal
        key={isCreating ? "create" : `edit-${editTarget?.id ?? "none"}`}
        open={isCreating || !!editTarget}
        onClose={() => {
          setIsCreating(false);
          setEditTarget(null);
        }}
        title={isCreating ? "Novo tenant" : "Editar tenant"}
        initial={editTarget}
        submitting={createMut.isPending || updateMut.isPending}
        onSubmit={(payload) => {
          if (editTarget) {
            updateMut.mutate({ id: editTarget.id, p: payload });
          } else {
            createMut.mutate(payload);
          }
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Deletar tenant?"
        message={
          deleteTarget
            ? `Isso vai apagar TUDO do tenant '${deleteTarget.name}': sites, ativos, devices, users. Histórico de readings fica em disco mas orfaniza. Ação irreversível.`
            : ""
        }
        confirmLabel="Deletar"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
      />
    </div>
  );
}

// ---------- form modal ------------------------------------------------------

function TenantFormModal({
  open,
  onClose,
  title,
  initial,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  initial?: Tenant | null;
  submitting?: boolean;
  onSubmit: (p: TenantCreate) => void;
}) {
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [name, setName] = useState(initial?.name ?? "");

  useEffect(() => {
    setSlug(initial?.slug ?? "");
    setName(initial?.name ?? "");
  }, [initial]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({ slug, name });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" form="tenant-form" loading={submitting}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="tenant-form" onSubmit={handleSubmit} className="space-y-4">
        <Field label="Nome" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="LAIT envpulse"
          />
        </Field>
        <Field
          label="Slug"
          required
        >
          <Input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            pattern="[a-zA-Z0-9_-]+"
            placeholder="acme-saude"
          />
        </Field>
      </form>
    </Modal>
  );
}
